import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { jsPDF } from 'jspdf';
import { Modal, Button } from 'react-bootstrap';
import logoImage from '../assets/Logo_black_text.png';
import { contextData } from "../ContextApi";

const MenuCalendar = ({ weeklyMenu, startDate, groupType }) => {
  const { userdata } = useContext(contextData);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSpecialDiet, setIsSpecialDiet] = useState(false);
  const [isFlytande, setIsFlytande] = useState(false);
  const [isTimbalGron, setIsTimbalGron] = useState(false);
  const [skipTimbalWithSoppa, setSkipTimbalWithSoppa] = useState(true);

  const dayOrder = {
    'Måndag': 1,
    'Tisdag': 2,
    'Onsdag': 3,
    'Torsdag': 4,
    'Fredag': 5,
    'Lördag': 6,
    'Söndag': 7,
  };

  if (!Array.isArray(weeklyMenu) || weeklyMenu.length === 0) {
    return <div className="alert-info">No menu data available</div>;
  }

  const cycleStartDate = new Date(startDate);
  const today = new Date();
  const msPerWeek = 7 * 24 * 60 * 60 * 1000;
  const totalWeeks = weeklyMenu.length;

  const weeksElapsed = Math.floor((today - cycleStartDate) / msPerWeek);
  const initialWeekOffset = weeksElapsed >= 0 ? weeksElapsed : 0;

  const [weekOffset, setWeekOffset] = useState(initialWeekOffset);
  const [menuIndex, setMenuIndex] = useState(initialWeekOffset % totalWeeks);

  const getWeekDateRange = (offset) => {
    const weekStart = new Date(cycleStartDate);
    weekStart.setDate(cycleStartDate.getDate() + offset * 7);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);

    const monthNames = [
      'Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni',
      'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'
    ];

    const startDay = weekStart.getDate();
    const startMonth = monthNames[weekStart.getMonth()];
    const endDay = weekEnd.getDate();
    const endMonth = monthNames[weekEnd.getMonth()];

    return `${startDay} ${startMonth} - ${endDay} ${endMonth}${weekStart.getFullYear() !== weekEnd.getFullYear() ? ` ${weekEnd.getFullYear()}` : ''}`;
  };

  const getWeekNumber = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const handlePrevWeek = () => {
    setWeekOffset((prevOffset) => prevOffset - 1);
    setMenuIndex((prevIndex) => (prevIndex - 1 + totalWeeks) % totalWeeks);
  };

  const handleNextWeek = () => {
    setWeekOffset((prevOffset) => prevOffset + 1);
    setMenuIndex((prevIndex) => (prevIndex + 1) % totalWeeks);
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const generatePDF = (specialDiet = false, flytande = false, timbalGron = false, skipTimbalWithSoppa = true) => {
    const doc = new jsPDF();
    const currentWeek = weeklyMenu[menuIndex];
    const menuData = currentWeek.days || [];
    const weekStart = new Date(cycleStartDate);
    weekStart.setDate(cycleStartDate.getDate() + weekOffset * 7);
    let yPosition = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const maxWidth = pageWidth - 2 * margin;
    const pageHeight = doc.internal.pageSize.getHeight();
    const bottomThreshold = pageHeight - 20;
  
    // Add logo image
    const kommun = userdata?.kommun || 'N/A';
    const formattedKommun = `${kommun.charAt(0).toUpperCase() + kommun.slice(1)}s kommun`;
    doc.addImage(logoImage, 'PNG', 168, 10, 20, 10);
    doc.setFontSize(10);
    doc.text(formattedKommun, 190, 25, { align: 'right' });
  
    // Header
    doc.setFontSize(14);
    doc.text(`Vecka: ${getWeekNumber(weekStart)}`, margin, yPosition);
    yPosition += 8;
  
    if (groupType && groupType.toLowerCase() === 'junior') {
      doc.setFontSize(10);
      doc.text('Skolan/Förskolan', margin, yPosition);
      yPosition += 8;
    } else if (groupType && groupType.toLowerCase() === 'senior') {
      doc.setFontSize(10);
      doc.text('Äldreboende', margin, yPosition);
      yPosition += 8;
    }
  
    // Menu list
    const sortedDays = [...menuData]
      .filter(day =>
        (day.lunch && day.lunch.some(meal => meal.dish)) ||
        (groupType && groupType.toLowerCase() === 'senior' && 
         ((day.supper && day.supper.length > 0) || (day.dessert && day.dessert.length > 0))
        )
      )
      .sort((a, b) => dayOrder[a.day] - dayOrder[b.day]);
    const textStartX = 40;
    const tagIconSize = 4;
  
    sortedDays.forEach((day, index) => {
      const dayDate = new Date(weekStart);
      dayDate.setDate(weekStart.getDate() + index);
      const month = String(dayDate.getMonth() + 1).padStart(2, '0');
      const dateString = `${dayDate.getDate()}/${month}, ${day.day}`;
  
      if (yPosition > bottomThreshold) {
        doc.addPage();
        yPosition = 20;
      }
  
      doc.setFontSize(10);
      doc.text(dateString, margin, yPosition);
  
      let mealYPosition = yPosition;
  
      // Lunch Section
      if (day.lunch && day.lunch.some(meal => meal.dish)) {
        mealYPosition += 2;
        const lunchLines = day.lunch.filter(meal => meal.dish).reduce((total, meal, mealIndex) => {
          const dishLines = doc.splitTextToSize(meal.dish, maxWidth - (textStartX + 10 + margin)).length;
          const sideLines = meal.side && meal.side.length > 0 ? doc.splitTextToSize(meal.side.join(', '), maxWidth - (textStartX + 10 + margin)).length : 0;
          let allergenText = '';
          if (specialDiet && mealIndex !== 1) {
            const allergenItems = [];
            if (meal.allergic && meal.allergic.length > 0) {
              allergenItems.push(...meal.allergic.map(item => item.toLowerCase() === 'soppa' ? `${item} (tillbehör)` : item));
            }
            if (flytande) {
              allergenItems.push('Flytande');
            }
            if (timbalGron && !(skipTimbalWithSoppa && meal.allergic && meal.allergic.some(allergen => allergen.toLowerCase() === 'soppa'))) {
              allergenItems.push('Timbal-grönsaker');
            }
            allergenText = allergenItems.join(', ');
          }
          const allergenLines = allergenText ? doc.splitTextToSize(allergenText, maxWidth - (textStartX + 10 + margin)).length : 0;
          return total + dishLines + sideLines + allergenLines + 2;
        }, 0);
        const lunchHeight = lunchLines * 5 + 10;
  
        if (mealYPosition + lunchHeight > bottomThreshold) {
          doc.addPage();
          mealYPosition = 20;
        }
  
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Lunch:', textStartX, mealYPosition);
        mealYPosition += 6;
  
        day.lunch
          .filter(meal => meal.dish)
          .forEach((meal, mealIndex) => {
            const altPrefix = `Alt ${mealIndex + 1}: `;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            const altWidth = doc.getTextWidth(altPrefix);
            doc.text(altPrefix, textStartX, mealYPosition);
  
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            const dishStartX = textStartX + altWidth + 10;
            const dishLines = doc.splitTextToSize(meal.dish, maxWidth - dishStartX + margin);
            dishLines.forEach((line, lineIndex) => {
              doc.text(line, dishStartX, mealYPosition + (lineIndex * 5));
            });
  
            if (meal.tagIcon) {
              const tagIconX = dishStartX + doc.getTextWidth(dishLines[0]) + 2;
              const tagIconY = mealYPosition - 3;
              try {
                doc.addImage(meal.tagIcon, 'PNG', tagIconX, tagIconY, tagIconSize, tagIconSize);
              } catch (error) {
                console.error(`Failed to add tagIcon for ${meal.dish}:`, error);
              }
            }
  
            let currentY = mealYPosition + (dishLines.length * 5);
  
            if (meal.side && meal.side.length > 0) {
              const sidePrefix = 'Tillbehör: ';
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(8);
              doc.text(sidePrefix, textStartX, currentY);
  
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(10);
              const sideStartX = dishStartX;
              const sideLines = doc.splitTextToSize(meal.side.join(', '), maxWidth - sideStartX + margin);
              sideLines.forEach((line, lineIndex) => {
                doc.text(line, sideStartX, currentY + (lineIndex * 5));
              });
              currentY += sideLines.length * 5;
            }
  
            let allergenText = '';
            if (specialDiet && mealIndex !== 1) {
              const allergenItems = [];
              if (meal.allergic && meal.allergic.length > 0) {
                allergenItems.push(...meal.allergic.map(item => item.toLowerCase() === 'soppa' ? `${item} (tillbehör)` : item));
              }
              if (flytande) {
                allergenItems.push('Flytande');
              }
              if (timbalGron && !(skipTimbalWithSoppa && meal.allergic && meal.allergic.some(allergen => allergen.toLowerCase() === 'soppa'))) {
                allergenItems.push('Timbal-grönsaker');
              }
              allergenText = allergenItems.join(', ');
            }
            if (allergenText) {
              const allergenPrefix = 'Specialkost: ';
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(8);
              doc.setTextColor(62, 135, 155); // Set color to #3e879b
              doc.text(allergenPrefix, textStartX, currentY);
  
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(10);
              doc.setCharSpace(0);
              const allergenStartX = dishStartX;
              const allergenLines = doc.splitTextToSize(allergenText, maxWidth - allergenStartX + margin);
              allergenLines.forEach((line, lineIndex) => {
                doc.text(line, allergenStartX, currentY + (lineIndex * 5));
              });
              doc.setTextColor(0, 0, 0); // Reset color to black
              currentY += allergenLines.length * 5;
            }
  
            mealYPosition = currentY + 2;
          });
      }
  
      // Supper Section (only for Senior)
      if (groupType && groupType.toLowerCase() === 'senior' && day.supper && day.supper.length > 0) {
        mealYPosition += 2;
        const supperLines = day.supper.filter(meal => meal.dish).reduce((total, meal, mealIndex) => {
          const dishLines = doc.splitTextToSize(meal.dish, maxWidth - (textStartX + 10 + margin)).length;
          const sideLines = meal.side && meal.side.length > 0 ? doc.splitTextToSize(meal.side.join(', '), maxWidth - (textStartX + 10 + margin)).length : 0;
          let allergenText = '';
          if (specialDiet && mealIndex !== 1) {
            const allergenItems = [];
            if (meal.allergic && meal.allergic.length > 0) {
              allergenItems.push(...meal.allergic.map(item => item.toLowerCase() === 'soppa' ? `${item} (tillbehör)` : item));
            }
            if (flytande) {
              allergenItems.push('Flytande');
            }
            if (timbalGron && !(skipTimbalWithSoppa && meal.allergic && meal.allergic.some(allergen => allergen.toLowerCase() === 'soppa'))) {
              allergenItems.push('Timbal-grönsaker');
            }
            allergenText = allergenItems.join(', ');
          }
          const allergenLines = allergenText ? doc.splitTextToSize(allergenText, maxWidth - (textStartX + 10 + margin)).length : 0;
          return total + dishLines + sideLines + allergenLines + 2;
        }, 0);
        const supperHeight = supperLines * 5 + 10;
  
        if (mealYPosition + supperHeight > bottomThreshold) {
          doc.addPage();
          mealYPosition = 20;
        }
  
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Middag:', textStartX, mealYPosition);
        mealYPosition += 6;
  
        day.supper
          .filter(meal => meal.dish)
          .forEach((meal, mealIndex) => {
            const altPrefix = `Alt ${mealIndex + 1}: `;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            const altWidth = doc.getTextWidth(altPrefix);
            doc.text(altPrefix, textStartX, mealYPosition);
  
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            const dishStartX = textStartX + altWidth + 10;
            const dishLines = doc.splitTextToSize(meal.dish, maxWidth - dishStartX + margin);
            dishLines.forEach((line, lineIndex) => {
              doc.text(line, dishStartX, mealYPosition + (lineIndex * 5));
            });
  
            if (meal.tagIcon) {
              const tagIconX = dishStartX + doc.getTextWidth(dishLines[0]) + 2;
              const tagIconY = mealYPosition - 3;
              try {
                doc.addImage(meal.tagIcon, 'PNG', tagIconX, tagIconY, tagIconSize, tagIconSize);
              } catch (error) {
                console.error(`Failed to add tagIcon for ${meal.dish}:`, error);
              }
            }
  
            let currentY = mealYPosition + (dishLines.length * 5);
  
            if (meal.side && meal.side.length > 0) {
              const sidePrefix = 'Tillbehör: ';
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(8);
              doc.text(sidePrefix, textStartX, currentY);
  
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(10);
              const sideStartX = dishStartX;
              const sideLines = doc.splitTextToSize(meal.side.join(', '), maxWidth - sideStartX + margin);
              sideLines.forEach((line, lineIndex) => {
                doc.text(line, sideStartX, currentY + (lineIndex * 5));
              });
              currentY += sideLines.length * 5;
            }
  
            let allergenText = '';
            if (specialDiet && mealIndex !== 1) {
              const allergenItems = [];
              if (meal.allergic && meal.allergic.length > 0) {
                allergenItems.push(...meal.allergic.map(item => item.toLowerCase() === 'soppa' ? `${item} (tillbehör)` : item));
              }
              if (flytande) {
                allergenItems.push('Flytande');
              }
              if (timbalGron && !(skipTimbalWithSoppa && meal.allergic && meal.allergic.some(allergen => allergen.toLowerCase() === 'soppa'))) {
                allergenItems.push('Timbal-grönsaker');
              }
              allergenText = allergenItems.join(', ');
            }
            if (allergenText) {
              const allergenPrefix = 'Specialkost: ';
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(8);
              doc.setTextColor(62, 135, 155); // Set color to #3e879b
              doc.text(allergenPrefix, textStartX, currentY);
  
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(10);
              doc.setCharSpace(0);
              const allergenStartX = dishStartX;
              const allergenLines = doc.splitTextToSize(allergenText, maxWidth - allergenStartX + margin);
              allergenLines.forEach((line, lineIndex) => {
                doc.text(line, allergenStartX, currentY + (lineIndex * 5));
              });
              doc.setTextColor(0, 0, 0); // Reset color to black
              currentY += allergenLines.length * 5;
            }
  
            mealYPosition = currentY + 2;
          });
      }
  
      // Dessert Section (only for Senior)
      if (groupType && groupType.toLowerCase() === 'senior' && day.dessert && day.dessert.length > 0) {
        mealYPosition += 2;
        const dessertLines = day.dessert.filter(meal => meal.name).reduce((total, meal, mealIndex) => {
          const dishLines = doc.splitTextToSize(meal.name, maxWidth - (textStartX + 10 + margin)).length;
          const sideLines = meal.side ? doc.splitTextToSize(meal.side, maxWidth - (textStartX + 10 + margin)).length : 0;
          let allergenText = '';
          if (specialDiet && mealIndex !== 1) {
            allergenText = meal.allergic && meal.allergic.length > 0 ? meal.allergic.map(item => item.toLowerCase() === 'soppa' ? `${item} (tillbehör)` : item).join(', ') : '';
          }
          const allergenLines = allergenText ? doc.splitTextToSize(allergenText, maxWidth - (textStartX + 10 + margin)).length : 0;
          return total + dishLines + sideLines + allergenLines + 2;
        }, 0);
        const dessertHeight = dessertLines * 5 + 10;
  
        if (mealYPosition + dessertHeight > bottomThreshold) {
          doc.addPage();
          mealYPosition = 20;
        }
  
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Efterrätt:', textStartX, mealYPosition);
        mealYPosition += 6;
  
        day.dessert
          .filter(meal => meal.name)
          .forEach((meal, mealIndex) => {
            const altPrefix = `Alt ${mealIndex + 1}: `;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            const altWidth = doc.getTextWidth(altPrefix);
            doc.text(altPrefix, textStartX, mealYPosition);
  
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            const dishStartX = textStartX + altWidth + 10;
            const dishLines = doc.splitTextToSize(meal.name, maxWidth - dishStartX + margin);
            dishLines.forEach((line, lineIndex) => {
              doc.text(line, dishStartX, mealYPosition + (lineIndex * 5));
            });
  
            if (meal.tagIcon) {
              const tagIconX = dishStartX + doc.getTextWidth(dishLines[0]) + 2;
              const tagIconY = mealYPosition - 3;
              try {
                doc.addImage(meal.tagIcon, 'PNG', tagIconX, tagIconY, tagIconSize, tagIconSize);
              } catch (error) {
                console.error(`Failed to add tagIcon for ${meal.name}:`, error);
              }
            }
  
            let currentY = mealYPosition + (dishLines.length * 5);
  
            if (meal.side) {
              const sidePrefix = 'Tillbehör: ';
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(8);
              doc.text(sidePrefix, textStartX, currentY);
  
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(10);
              const sideStartX = dishStartX;
              const sideLines = doc.splitTextToSize(meal.side, maxWidth - sideStartX + margin);
              sideLines.forEach((line, lineIndex) => {
                doc.text(line, sideStartX, currentY + (lineIndex * 5));
              });
              currentY += sideLines.length * 5;
            }
  
            let allergenText = '';
            if (specialDiet && mealIndex !== 1) {
              allergenText = meal.allergic && meal.allergic.length > 0 ? meal.allergic.map(item => item.toLowerCase() === 'soppa' ? `${item} (tillbehör)` : item).join(', ') : '';
            }
            if (allergenText) {
              const allergenPrefix = 'Specialkost: ';
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(8);
              doc.setTextColor(62, 135, 155); // Set color to #3e879b
              doc.text(allergenPrefix, textStartX, currentY);
  
              doc.setFont('helvetica', 'normal');
              doc.setFontSize(10);
              doc.setCharSpace(0);
              const allergenStartX = dishStartX;
              const allergenLines = doc.splitTextToSize(allergenText, maxWidth - allergenStartX + margin);
              allergenLines.forEach((line, lineIndex) => {
                doc.text(line, allergenStartX, currentY + (lineIndex * 5));
              });
              doc.setTextColor(0, 0, 0); // Reset color to black
              currentY += allergenLines.length * 5;
            }
  
            mealYPosition = currentY + 2;
          });
      }
  
      yPosition = mealYPosition + 2;
    });
  
    doc.autoPrint();
    const pdfUrl = doc.output('bloburl');
    window.open(pdfUrl);
    closeModal();
  };
  const currentWeek = weeklyMenu[menuIndex];
  const menuData = currentWeek.days || [];
  const sortedDays = [...menuData]
    .filter(day =>
      (day.lunch && day.lunch.some(meal => meal.dish)) ||
      (groupType && groupType.toLowerCase() === 'senior' && 
       ((day.supper && day.supper.length > 0) || (day.dessert && day.dessert.length > 0))
      )
    )
    .sort((a, b) => dayOrder[a.day] - dayOrder[b.day]);

  const isSenior = groupType && groupType.toLowerCase() === 'senior';
  const isJunior = groupType && groupType.toLowerCase() === 'junior';
  return (
    <div className="menu-calendar m-auto">
      <div className="carousel-header p-2">
        <button className="nav-button prev" onClick={handlePrevWeek}>
          <i className="fa-solid fa-chevron-left"></i>
        </button>
        <h2 className="carousel-title p-2" style={{ backgroundColor: currentWeek.weekColor || '#ffffff' }}>
          {getWeekDateRange(weekOffset)}
          <span className="font-size-s"> Vecka {
          getWeekNumber(new Date(cycleStartDate.getTime() + weekOffset * 7 * 24 * 60 * 60 * 1000))}</span>
        </h2>
        <button className="nav-button next" onClick={handleNextWeek}>
          <i className="fa-solid fa-chevron-right"></i>
        </button>
        {isSenior && (
          <button className="print-button" onClick={openModal}>
            <i className="fa-solid fa-print glow me-2"></i>Utskrift
          </button>
        )}
        {isJunior && (
          <button className="print-button" onClick={() => generatePDF()}>
            <i className="fa-solid fa-print glow me-2"></i>Utskrift
          </button>
        )}
      </div>

      {isSenior && (
        <Modal
          show={isModalOpen}
          onHide={closeModal}
          centered
          className="modal-overlay"
        >
          <Modal.Header closeButton>
            <Modal.Title className="modal-title">Välj utskriftsalternativ</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="modal-content p-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  id="specialDiet"
                  checked={isSpecialDiet}
                  onChange={(e) => setIsSpecialDiet(e.target.checked)}
                  className="form-check-input modal-checkbox"
                />
                <label htmlFor="specialDiet" className="form-check-label">
                  Specialkost (Inkludera Specialkost)
                </label>
              </div>
              <div className="form-check mt-2">
                <input
                  type="checkbox"
                  id="flytande"
                  checked={isFlytande}
                  onChange={(e) => setIsFlytande(e.target.checked)}
                  className="form-check-input modal-checkbox"
                />
                <label htmlFor="flytande" className="form-check-label">
                  Flytande
                </label>
              </div>
              <div className="form-check mt-2">
                <input
                  type="checkbox"
                  id="timbalGron"
                  checked={isTimbalGron}
                  onChange={(e) => setIsTimbalGron(e.target.checked)}
                  className="form-check-input modal-checkbox"
                />
                <label htmlFor="timbalGron" className="form-check-label">
                  Timbal-grönsaker
                </label>
              </div>
              <div className="form-check mt-2">
                <input
                  type="checkbox"
                  id="skipTimbalWithSoppa"
                  checked={skipTimbalWithSoppa}
                  onChange={(e) => setSkipTimbalWithSoppa(e.target.checked)}
                  className="form-check-input modal-checkbox"
                />
                <label htmlFor="skipTimbalWithSoppa" className="form-check-label">
                  Hoppa över Timbal-grönsaker vid soppa
                </label>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="modal-footer">
            <div>
              <Button
                variant="secondary"
                onClick={closeModal}
                className="modal-button cancel btn mt-2"
              >
                Avbryt
              </Button>
              <Button
                variant="primary"
                className="modal-button confirm btn mt-2 ms-2"
                onClick={() => generatePDF(isSpecialDiet, isFlytande, isTimbalGron, skipTimbalWithSoppa)}
              >
                Skriv ut
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}

      <div className="week-section">
        <div className="calendar-grid">
          {sortedDays.length > 0 ? (
            sortedDays.map((day, index) => (
              <div key={index} className="day-column">
                <h3>{day.day}</h3>
                <div className="meal-options">
                  {/* Lunch Section */}
                  {day.lunch && day.lunch.length > 0 && (
                    <>
                      <h5 className="font-size-s">Lunch</h5>
                      {day.lunch
                        .filter(meal => meal.dish)
                        .map((meal, mealIndex) => (
                          <div key={`lunch-${mealIndex}`} className="meal-row">
                            <span className="meal-number font-size-xs">Alt:{mealIndex + 1}</span>
                            <div className="meal-content">
                              <div className="dish-wrapper">
                                <span className="meal-text font-size-xs" title={meal.dish}>
                                  {meal.dish}
                                </span>
                                {meal.tagIcon && <img className="tagIcon" src={meal.tagIcon} alt="tagIcon" />}
                              </div>
                              {meal.side && meal.side.length > 0 && (
                                <span className="meal-side font-size-xs me-4" title={meal.side.join(', ')}>
                                  Tillbehör: {meal.side.join(', ')}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                    </>
                  )}
                  {/*Invert colors Supper Section (only for Senior) */}
                  {groupType && groupType.toLowerCase() === 'senior' && day.supper && day.supper.length > 0 && (
                    <>
                      <h5 className="font-size-s">Middag</h5>
                      {day.supper
                        .filter(meal => meal.dish)
                        .map((meal, mealIndex) => (
                          <div key={`supper-${mealIndex}`} className="meal-row">
                            <span className="meal-number font-size-xs">Alt:{mealIndex + 1}</span>
                            <div className="meal-content">
                              <div className="dish-wrapper">
                                <span className="meal-text font-size-xs" title={meal.dish}>
                                  {meal.dish}
                                </span>
                                {meal.tagIcon && <img className="tagIcon" src={meal.tagIcon} alt="tagIcon" />}
                              </div>
                              {meal.side && meal.side.length > 0 && (
                                <span className="meal-side font-size-xs me-4" title={meal.side.join(', ')}>
                                  Tillbehör: {meal.side.join(', ')}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                    </>
                  )}
                  {/* Dessert Section (only for Senior) */}
                  {groupType && groupType.toLowerCase() === 'senior' && day.dessert && day.dessert.length > 0 && (
                    <>
                      <h5 className="font-size-s">Efterrätt</h5>
                      {day.dessert
                        .filter(meal => meal.name)
                        .map((meal, mealIndex) => (
                          <div key={`dessert-${mealIndex}`} className="meal-row">
                            <span className="meal-number font-size-xs">Alt:{mealIndex + 1}</span>
                            <div className="meal-content">
                              <div className="dish-wrapper">
                                <span className="meal-text font-size-xs" title={meal.name}>
                                  {meal.name}
                                </span>
                                {meal.tagIcon && <img className="tagIcon" src={meal.tagIcon} alt="tagIcon" />}
                              </div>
                              {meal.extraSideIngredient && (
                                <span className="meal-side font-size-xs me-4" title={meal.extraSideIngredient}>
                                  Tillbehör: {meal.extraSideIngredient}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                    </>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="alert alert-light">No meals available for this week</div>
          )}
        </div>
      </div>
    </div>
  );
};

MenuCalendar.propTypes = {
  weeklyMenu: PropTypes.arrayOf(
    PropTypes.shape({
      weekNumber: PropTypes.number.isRequired,
      weekName: PropTypes.string,
      weekColor: PropTypes.string,
      days: PropTypes.arrayOf(
        PropTypes.shape({
          day: PropTypes.string.isRequired,
          lunch: PropTypes.arrayOf(
            PropTypes.shape({
              alt: PropTypes.number,
              dish: PropTypes.string,
              side: PropTypes.arrayOf(PropTypes.string),
              tagIcon: PropTypes.string,
              allergic: PropTypes.arrayOf(PropTypes.string)
            })
          ),
          supper: PropTypes.arrayOf(
            PropTypes.shape({
              alt: PropTypes.number,
              dish: PropTypes.string,
              side: PropTypes.arrayOf(PropTypes.string),
              tagIcon: PropTypes.string,
              allergic: PropTypes.arrayOf(PropTypes.string)
            })
          ),
          dessert: PropTypes.arrayOf(
            PropTypes.shape({
              name: PropTypes.string,
              tagIcon: PropTypes.string,
              extraSideIngredient: PropTypes.string,
              allergic: PropTypes.arrayOf(PropTypes.string)
            })
          ),
          lunchDishDetails: PropTypes.arrayOf(
            PropTypes.shape({
              _id: PropTypes.string,
              name: PropTypes.string,
              tagIcon: PropTypes.string,
              completelyDish: PropTypes.string,
              extraSideIngredient: PropTypes.string,
              allergic: PropTypes.arrayOf(PropTypes.string)
            })
          ),
          supperDishDetails: PropTypes.arrayOf(
            PropTypes.shape({
              _id: PropTypes.string,
              name: PropTypes.string,
              tagIcon: PropTypes.string,
              completelyDish: PropTypes.string,
              extraSideIngredient: PropTypes.string,
              allergic: PropTypes.arrayOf(PropTypes.string)
            })
          ),
          dessertDishDetails: PropTypes.arrayOf(
            PropTypes.shape({
              _id: PropTypes.string,
              name: PropTypes.string,
              tagIcon: PropTypes.string,
              completelyDish: PropTypes.string,
              extraSideIngredient: PropTypes.string,
              allergic: PropTypes.arrayOf(PropTypes.string)
            })
          )
        })
      )
    })
  ).isRequired,
  startDate: PropTypes.string,
  groupType: PropTypes.string
};

export default MenuCalendar;